// People → Users on the staff surface (REQ-241, SCR-591): the read-only table of
// GET /v1/orgs/{orgId}/users. Users are auto-provisioned the first time the proxy sees them (AWS
// sign-in, OIDC, API keys), so this is who has used the proxy, by name, with their team by name
// (never its uuid, IA-1). Moved from the billing admin's Users & teams tab (TASK-692); the Teams
// screen is People's own `teams` sub-tab now.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import type { components } from "../../api/schema";
import { Card, Chip, type ChipTone } from "../../primitives/page";
import { DataTable, type Column } from "../../primitives/table";
import { dayLabel } from "../usage/figures";
import { personName, UNKNOWN_TEAM } from "../usage/names";

type User = components["schemas"]["User"];
type Team = components["schemas"]["Team"];

/** The page sizes the list reads with (API `limit` max 500). */
const USERS_PAGE = 200;
const TEAMS_PAGE = 500;

export interface UserRow {
  id: string;
  /** `personName`: the display name, else the email, else an AWS principal's name, else "Unknown user". */
  name: string;
  email: string | null;
  team: string;
  status: { label: string; tone: ChipTone };
  canReadPrompts: boolean;
  createdAt: string;
}

const USER_STATUS: Readonly<Record<User["status"], { label: string; tone: ChipTone }>> = {
  active: { label: "Active", tone: "green" },
  disabled: { label: "Disabled", tone: "amber" },
  erased: { label: "Erased", tone: "grey" },
};

/** A user as the table shows it; `teamNames` maps a team's uuid to its name. */
export function userRow(user: User, teamNames: ReadonlyMap<string, string>): UserRow {
  return {
    id: user.id,
    // Never the external id as it is: an OIDC, Cognito or trusted-token person's is
    // `<issuer hash>:<sub>` and a sub is often a uuid (IA-1); an AWS principal is named from it.
    name: personName(user),
    email: user.email,
    team: user.teamId === null ? "No team" : (teamNames.get(user.teamId) ?? UNKNOWN_TEAM),
    status: USER_STATUS[user.status],
    canReadPrompts: user.canReadPrompts,
    createdAt: user.createdAt,
  };
}

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

const USER_COLUMNS: Column<UserRow>[] = [
  {
    key: "name",
    header: "User",
    cell: (row) => row.name,
    compare: (a, b) => a.name.localeCompare(b.name),
  },
  { key: "email", header: "Email", cell: (row) => row.email ?? "—" },
  { key: "team", header: "Team", help: "customers.team", cell: (row) => row.team, compare: (a, b) => a.team.localeCompare(b.team) },
  {
    key: "status",
    header: "Status",
    cell: (row) => <Chip tone={row.status.tone}>{row.status.label}</Chip>,
    compare: (a, b) => a.status.label.localeCompare(b.status.label),
  },
  { key: "prompts", header: "Reads prompts", help: "customers.canReadPrompts", cell: (row) => (row.canReadPrompts ? "Yes" : "No") },
  { key: "since", header: "First seen", cell: (row) => dayLabel(row.createdAt), compare: (a, b) => a.createdAt.localeCompare(b.createdAt) },
];

export function UsersList({ context }: { context: OrgContext }) {
  const client = useApi();
  const { orgId } = context;
  const [users, setUsers] = useState<User[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [teamNames, setTeamNames] = useState<ReadonlyMap<string, string>>(new Map());

  const loadUsers = (after: string | null, replace: boolean) => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/users", { path: { orgId }, query: after ? { cursor: after, limit: USERS_PAGE } : { limit: USERS_PAGE } })
      .then((page) => {
        setUsers((current) => (replace ? page.items : [...current, ...page.items]));
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers(null, true);
    // The Team column's names only; a failed read leaves each team "Unknown team", never its id.
    client
      .get("/v1/orgs/{orgId}/teams", { path: { orgId }, query: { limit: TEAMS_PAGE } })
      .then((page) => setTeamNames(new Map(page.items.map((team: Team) => [team.id, team.name]))))
      .catch(() => setTeamNames(new Map()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId]);

  return (
    <Card title="Users" help="customers.users">
      <p class="card-note">Everyone who has used the proxy for this customer. Users appear the first time they sign in with AWS, an OIDC token or an API key.</p>
      <DataTable
        caption="Users"
        hideCaption
        columns={USER_COLUMNS}
        rows={users.map((user) => userRow(user, teamNames))}
        rowKey={(row) => row.id}
        loading={loading}
        error={error}
        onRetry={() => loadUsers(null, true)}
        emptyMessage="No users yet. They appear here after their first request through the proxy."
        nextCursor={cursor}
        onLoadMore={() => loadUsers(cursor, false)}
      />
    </Card>
  );
}
